import { type ReactNode } from 'react';
import clsx from 'clsx';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { i18n } from '@/shared/i18n';

/**
 * Generic copy shown when a control is disabled for lack of a project permission. A function
 * rather than a constant: seven modules import this, and a value resolved at import would
 * freeze the language chosen before i18next finished loading.
 */
export const getNoPermissionMessage = (): string =>
  i18n.t('common:components.permissionTooltip.noPermission');

interface PermissionTooltipProps {
  /**
   * When true, wrap `children` in a tooltip explaining the missing permission.
   * The child should already be rendered in its disabled state — the wrapping
   * span guarantees hover events fire even though a disabled control swallows
   * pointer events.
   */
  disabled: boolean;
  message?: string;
  children: ReactNode;
  /** Extra classes merged onto the wrapping tooltip-trigger span. */
  className?: string;
}

export function PermissionTooltip({
  disabled,
  message,
  children,
  className,
}: PermissionTooltipProps) {
  if (!disabled) return <>{children}</>;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={clsx('cursor-not-allowed', className)}>{children}</span>
        </TooltipTrigger>
        <TooltipContent>{message ?? getNoPermissionMessage()}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
